import type { ComponentProps, ReactNode, Ref } from 'react';
import { useSyncExternalStore } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type {
	ToastProps as AriaToastProps,
	ToastRegionProps as AriaToastRegionProps,
	ToastOptions,
} from 'react-aria-components/Toast';
import {
	UNSTABLE_Toast as AriaToast,
	UNSTABLE_ToastContent as AriaToastContent,
	UNSTABLE_ToastQueue as AriaToastQueue,
	UNSTABLE_ToastRegion as AriaToastRegion,
	Text,
} from 'react-aria-components/Toast';
import { PressResponder } from '@react-aria/interactions';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { StatusIcon } from '@launchpad-ui/icons';

import { IconButton } from './IconButton';

import styles from './styles/Toast.module.css';

const toastRegionStyles = cva(styles.region, {
	variants: {
		placement: {
			bottom: styles.bottom,
			right: styles.right,
		},
	},
	defaultVariants: {
		placement: 'bottom',
	},
});

const iconStyles = cva(styles.icon, {
	variants: {
		status: {
			error: styles.error,
			info: styles.info,
			success: styles.success,
		},
	},
	defaultVariants: {
		status: 'info',
	},
});

const toastStyles = cva(styles.toast, {
	variants: {
		variant: {
			default: styles.default,
			snackbar: styles.snackbar,
		},
	},
	defaultVariants: {
		variant: 'default',
	},
});

interface RegionVariants extends VariantProps<typeof toastRegionStyles> {}
interface IconVariants extends VariantProps<typeof iconStyles> {}
interface ToastVariants extends VariantProps<typeof toastStyles> {}

interface ToastValue {
	title?: ReactNode;
	description?: ReactNode;
	action?: ReactNode;
}

interface LPToastContent extends ToastValue, IconVariants {}

interface ToastProps<T> extends AriaToastProps<T>, ToastVariants {
	ref?: Ref<HTMLDivElement>;
	queue?: ToastQueue<T>;
}

interface ToastRegionProps<T> extends Partial<Omit<AriaToastRegionProps<T>, 'queue'>>, RegionVariants {
	queue?: ToastQueue<T>;
}

/** Keep in sync with the toast exit animation duration in `Toast.module.css`. */
const exitDuration = 200;

const prefersReducedMotion = () =>
	typeof window === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * A toast queue that keeps a closed toast mounted, marked with `data-exiting`, for the duration of
 * its exit animation before removing it.
 *
 * https://react-spectrum.adobe.com/react-aria/Toast.html
 */
class ToastQueue<T> extends AriaToastQueue<T> {
	private exitingKeys = new Set<string>();
	private exitSubscriptions = new Set<() => void>();

	subscribeExiting = (fn: () => void) => {
		this.exitSubscriptions.add(fn);
		return () => {
			this.exitSubscriptions.delete(fn);
		};
	};

	isExiting = (key: string) => this.exitingKeys.has(key);

	close(key: string): void {
		if (this.exitingKeys.has(key)) {
			return;
		}

		if (prefersReducedMotion()) {
			super.close(key);
			return;
		}

		this.exitingKeys.add(key);
		for (const fn of this.exitSubscriptions) {
			fn();
		}

		setTimeout(() => {
			this.exitingKeys.delete(key);
			super.close(key);
		}, exitDuration);
	}
}

class LPToastQueue<T> extends ToastQueue<T> {
	add(content: T, options?: ToastOptions): string {
		return super.add(content, { ...options, timeout: 6000 });
	}
}

const noopSubscribe = () => () => {};

const useIsExiting = <T,>(queue: ToastQueue<T> | undefined, key: string) =>
	useSyncExternalStore(
		queue?.subscribeExiting ?? noopSubscribe,
		() => queue?.isExiting(key) ?? false,
		() => false,
	);

const toastQueue = new LPToastQueue<LPToastContent>({
	maxVisibleToasts: 5,
});

const snackbarQueue = new ToastQueue<LPToastContent>({
	maxVisibleToasts: 5,
});

/**
 * A Toast displays a brief, temporary notification of actions, errors, or other events in an application.
 *
 * https://react-spectrum.adobe.com/react-aria/Toast.html
 */
const Toast = ({ ref, variant, queue, ...props }: ToastProps<LPToastContent>) => {
	const isExiting = useIsExiting(queue, props.toast.key);

	return (
		<AriaToast
			{...props}
			ref={ref}
			data-exiting={isExiting || undefined}
			className={composeRenderProps(props.className, (className, renderProps) =>
				toastStyles({ ...renderProps, className, variant }),
			)}
			data-theme="dark"
		>
			{composeRenderProps(props.children, (children, { toast }) => (
				<>
					<StatusIcon kind={toast.content.status || 'info'} className={iconStyles({ status: toast.content.status })} />
					<ToastContent>
						<Text slot="title">{toast.content.title}</Text>
						<Text slot="description">
							{toast.content.description}
							{toast.content.action && (
								<PressResponder
									onPress={() => (variant === 'default' ? toastQueue.close(toast.key) : snackbarQueue.close(toast.key))}
								>
									{' '}
									{toast.content.action}
								</PressResponder>
							)}
						</Text>
						{children}
					</ToastContent>
					{/* @ts-expect-error RAC adds label */}
					<IconButton size="small" variant="minimal" icon="cancel" slot="close" />
				</>
			))}
		</AriaToast>
	);
};

/**
 * ToastContent wraps the main content of a toast notification.
 *
 * https://react-spectrum.adobe.com/react-aria/Toast.html
 */
const ToastContent = (props: ComponentProps<typeof AriaToastContent>) => {
	return <AriaToastContent {...props} className={styles.content} />;
};

/**
 * A ToastRegion displays one or more toast notifications.
 *
 * https://react-spectrum.adobe.com/react-aria/Toast.html
 */
const ToastRegion = ({
	placement = 'bottom',
	queue = toastQueue,
	children,
	...props
}: ToastRegionProps<LPToastContent>) => {
	return (
		<AriaToastRegion
			queue={queue}
			className={composeRenderProps(props.className, (className, renderProps) =>
				toastRegionStyles({ ...renderProps, className, placement }),
			)}
			{...props}
		>
			{children ?? (({ toast }) => <Toast toast={toast} queue={queue} />)}
		</AriaToastRegion>
	);
};

/**
 * A SnackbarRegion displays one or more snackbar notifications.
 *
 * https://react-spectrum.adobe.com/react-aria/Toast.html
 */
const SnackbarRegion = ({
	placement = 'right',
	queue = snackbarQueue,
	children,
	...props
}: ToastRegionProps<LPToastContent>) => {
	return (
		<AriaToastRegion
			queue={queue}
			className={composeRenderProps(props.className, (className, renderProps) =>
				toastRegionStyles({ ...renderProps, className, placement }),
			)}
			{...props}
		>
			{children ?? (({ toast }) => <Toast toast={toast} queue={queue} variant="snackbar" />)}
		</AriaToastRegion>
	);
};

export {
	snackbarQueue,
	toastQueue,
	Toast,
	ToastContent,
	ToastQueue,
	ToastRegion,
	SnackbarRegion,
	toastStyles,
	toastRegionStyles,
};
export type { ToastOptions, ToastValue };
